import React, { useMemo, useState } from 'react';
import { useParticipantEPR } from './ParticipantEPRContext';
import { useSession } from './SessionContext';
import dayjs from 'dayjs';
import { Box, Paper, Button } from '@mui/material';

const ACCENT_BORDER = '#60A5FA';     // frame
const GREY_HEADER = '#e5e7eb';       // head + left label cells

const obsBands = {
  "Respiration rate": [
    { label: "≥25",   min: 25,        score: 3, color: 'red' },
    { label: "21–24", min: 21, max:24,score: 2, color: 'orange' },
    { label: "12–20", min: 12, max:20,score: 0 },
    { label: "9–11",  min: 9,  max:11,score: 1, color: 'yellow' },
    { label: "≤8",    max: 8,         score: 3, color: 'red' }
  ],
  "SpO₂ scale": [
    { label: "≥96",   min: 96,        score: 0 },
    { label: "94–95", min: 94, max:95,score: 1, color: 'yellow' },
    { label: "92–93", min: 92, max:93,score: 2, color: 'orange' },
    { label: "≤91",   max: 91,        score: 3, color: 'red' }
  ],
  "Systolic BP": [
    { label: "≥220",  min: 220,       score: 3, color: 'red' },
    { label: "111–219", min:111, max:219, score:0 },
    { label: "101–110", min:101, max:110, score:1, color:'yellow' },
    { label: "91–100",  min:91,  max:100, score:2, color:'orange' },
    { label: "≤90",     max:90,        score:3, color:'red' }
  ],
  "Pulse": [
    { label: "≥131",   min: 131,       score: 3, color: 'red' },
    { label: "111–130",min:111, max:130,score:2, color:'orange' },
    { label: "91–110", min:91,  max:110, score:1, color:'yellow' },
    { label: "51–90",  min:51,  max:90,  score:0 },
    { label: "41–50",  min:41,  max:50,  score:1, color:'yellow' },
    { label: "≤40",    max:40,         score:3, color:'red' }
  ],
  "Temperature": [
    { label: "≥39.1",  min: 39.1,      score: 2, color: 'orange' },
    { label: "38.1–39.0", min:38.1,max:39.0, score:1, color:'yellow' },
    { label: "36.1–38.0", min:36.1,max:38.0, score:0 },
    { label: "35.1–36.0", min:35.1,max:36.0, score:1, color:'yellow' },
    { label: "≤35.0",  max:35.0,       score: 3, color: 'red' }
  ],
  "AVPU": [
    { label: "Alert", match: "Alert", score: 0 },
    { label: "V,P,U", match: ["V","P","U"], score: 3, color: 'red' }
  ]
};

const keyMap = {
  "Respiration rate": "rr",
  "SpO₂ scale": "spo2",
  "Systolic BP": "sbp",
  "Pulse": "pulse",
  "Temperature": "temp",
  "AVPU": "avpu",
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const isOnOxygen = (obs) => {
  if (!obs) return false;
  const raw = String(obs.o2 ?? '').trim();
  if (!raw) return false;
  const m = raw.match(/[\d.]+/);
  const liters = m ? parseFloat(m[0]) : NaN;
  return Number.isFinite(liters) && liters > 0;
};

function getScore(bands, value) {
  for (const band of bands) {
    if (band.match) {
      if (Array.isArray(band.match)) {
        if (band.match.includes(value)) return band.score;
      } else {
        if (band.match === value) return band.score;
      }
    } else {
      if (value === undefined) continue;
      const aboveMin = band.min === undefined || value >= band.min;
      const belowMax = band.max === undefined || value <= band.max;
      if (aboveMin && belowMax) return band.score;
    }
  }
  return 0;
}

const bandTextColor = (bg) => (bg === 'red' ? '#fff' : '#111');
const fmtValue = (label, raw) => {
  if (raw == null || raw === '') return '';
  if (label === 'SpO₂ scale') return `${raw}%`;
  if (label === 'Temperature') return `${raw}°C`;
  return String(raw);
};

export default function NewsTable() {
  const { eprData } = useParticipantEPR();
  const { socket, room, liveId } = useSession();
  const [sending, setSending] = useState(false);
  const API_BASE = process.env.REACT_APP_API_URL;
  const rawObs = eprData?.eprObs ?? [];

  const sendRequest = async() => {
    if (!socket || !room || !liveId) return;

        try {
          setSending(true)
          await fetch(`${API_BASE}/api/requestobs`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
              livescenarioid: liveId,
              room,
              }),
          });
        } catch (err) {
          console.error('Error requesting obs:', err);
          alert('Failed to request obs. Please try again.');
        } finally {
        setSending(false);
        }
  };

const { columns, headers } = useMemo(() => {
  const sorted = [...rawObs]
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    .map(o => ({
      ...o,
      rr: num(o.rr),
      spo2: num(o.spo2),
      sbp: num(o.sbp),
      pulse: num(o.pulse),
      temp: num(o.temp),
    }));

  const MAX_COLS = 10;
  const MIN_COLS = 7;
  const last = sorted.slice(-MAX_COLS);
  const pad = Math.max(0, MIN_COLS - last.length);
  const columns = [...last, ...Array(pad).fill(null)];
  const headers = columns.map((c) => (c ? dayjs(c.timestamp).format('DD/MM/YY HH:mm') : ''));

  return { columns, headers };
}, [rawObs]);

  

  const params = ["Respiration rate", "SpO₂ scale", "Air or Oxygen", "Systolic BP", "Pulse", "AVPU", "Temperature"];

  // tighter cells
  const cellBase = {
    border: '1px solid #000',
    padding: '2px 4px',
    textAlign: 'center',
    lineHeight: 1.1,
    fontSize: '0.8rem'
  };
  const labelCellBase = {
    ...cellBase,
    backgroundColor: GREY_HEADER,
    fontWeight: 'bold'
  };

  const renderRow = (label, bands) => (
    <React.Fragment key={label}>
      {bands.map((band, rowIdx) => {
        const rowBg = band.color || 'white'; // whole row coloured
        const rowColor = bandTextColor(band.color);

        return (
          <tr key={label + '-' + band.label} style={{ backgroundColor: rowBg, color: rowColor }}>
            {/* fixed grey left label once per parameter */}
            {rowIdx === 0 && (
              <td rowSpan={bands.length} style={{ ...labelCellBase, backgroundColor: GREY_HEADER, color: '#111', width: 140 }}>
                {label}
              </td>
            )}

            {/* band label (inherits row colour) */}
            <td style={{ ...cellBase, fontWeight: 600, backgroundColor: rowBg, color: rowColor }}>
              {band.label}
            </td>

            {/* data cells: value only in the matching band; others blank, but row stays coloured */}
            {columns.map((obs, i) => {
              const raw = obs?.[keyMap[label]];
              const value = label === 'AVPU' ? raw : num(raw);

              let match = false;
              if (band.match) {
                match = Array.isArray(band.match) ? band.match.includes(raw) : band.match === raw;
              } else if (value !== undefined) {
                const aboveMin = band.min === undefined || value >= band.min;
                const belowMax = band.max === undefined || value <= band.max;
                match = aboveMin && belowMax;
              }

              return (
                <td
                  key={i + '-' + label}
                  style={{
                    ...cellBase,
                    backgroundColor: rowBg,
                    color: match ? rowColor : rowColor, // same row text colour
                    fontWeight: match ? 700 : 400
                  }}
                >
                  {obs ? (match ? fmtValue(label, raw) : '') : ''}
                </td>
              );
            })}
          </tr>
        );
      })}
      {/* tiny spacer */}
      <tr><td colSpan={2 + columns.length} style={{ border: 'none', height: 2 }} /></tr>
    </React.Fragment>
  );

  const renderAirO2 = () => (
    <React.Fragment>
      {/* Air row (white) */}
      <tr style={{ backgroundColor: 'white', color: '#111' }}>
        <td rowSpan={3} style={{ ...labelCellBase, backgroundColor: GREY_HEADER, color: '#111' }}>
          Air or Oxygen
        </td>
        <td style={{ ...cellBase, fontWeight: 600 }}>Air</td>
        {columns.map((o, i) => (
          <td key={i + '-air'} style={cellBase}>
            {o ? (!isOnOxygen(o) ? 'Air' : '') : ''}
          </td>
        ))}
      </tr>

      {/* O2 L/min row (whole row orange) */}
      <tr style={{ backgroundColor: 'orange', color: '#111' }}>
        <td style={{ ...cellBase, fontWeight: 700, backgroundColor: 'orange' }}>O₂ L/min</td>
        {columns.map((o, i) => (
          <td
            key={i + '-o2'}
            style={{ ...cellBase, backgroundColor: 'orange', fontWeight: o && isOnOxygen(o) ? 800 : 500 }}
          >
            {o?.o2 || ''}
          </td>
        ))}
      </tr>

      {/* Device row (white) */}
      <tr style={{ backgroundColor: 'white', color: '#111' }}>
        <td style={{ ...cellBase, fontWeight: 600 }}>Device</td>
        {columns.map((o, i) => (
          <td key={i + '-device'} style={cellBase}>
            {o?.device || ''}
          </td>
        ))}
      </tr>

      <tr><td colSpan={2 + columns.length} style={{ border: 'none', height: 2 }} /></tr>
    </React.Fragment>
  );

  const renderScoreRow = () => (
    <tr>
      <td colSpan={2} style={{ ...labelCellBase, borderRight: '1px solid #000' }}>Total NEWS</td>
      {columns.map((obs, i) => {
        if (!obs) return <td key={i} style={cellBase} />;
        let score = 0;
        for (const param of ["Respiration rate", "SpO₂ scale", "Systolic BP", "Pulse", "AVPU", "Temperature"]) {
          const key = keyMap[param];
          const raw = obs[key];
          const value = (param === 'AVPU') ? raw : num(raw);
          score += getScore(obsBands[param], value);
        }
        if (isOnOxygen(obs)) score += 2;
        return <td key={i} style={{ ...cellBase, fontWeight: 800 }}>{score}</td>;
      })}
    </tr>
  );

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${ACCENT_BORDER}`, borderRadius: 3, overflow: 'hidden', bgcolor: 'white' }}>
      {/* Top bar */}
        <Box sx={{
          px: 2, py: 1,
          backgroundColor: '#dbeafe',
          borderBottom: `1px solid ${ACCENT_BORDER}`, borderTopRightRadius:'12px', borderTopLeftRadius:'12px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box sx={{ fontWeight: 750, fontSize:'20px' }}>NEWS Chart</Box>
          <Button variant="contained" size="small" onClick={sendRequest} disabled={sending} sx={{ textTransform: 'none', boxShadow: 'none' }}>
            {sending ? 'Sending…' : 'Request'}
          </Button>
      </Box>

      <Box
        sx={{
          p: 2,
          overflow: 'auto',        // vertical + horizontal scroll
          minHeight: 0,            // flex child can shrink within parent
          maxHeight: 'calc(100vh - 180px)' // adjust if your header area is taller/shorter
        }}
      >
        <table
          style={{
            width: '100%',
            tableLayout: 'fixed',
            fontSize: '0.85rem',
            borderCollapse: 'collapse',     // single grid (fixes thick/double)
            border: '1px solid #000'
          }}
        >
          <colgroup>
            <col style={{ width: 140 }} />
            <col style={{ width: 100 }} />
            {columns.map((_, i) => <col key={i} />)}
          </colgroup>

          <thead>
            <tr>
              <th style={{ ...labelCellBase }}></th>
              <th style={{ ...labelCellBase }}></th>
              {headers.map((dt, i) => (
                <th key={i} style={{ ...labelCellBase }}>{dt}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {params.map((param) =>
              param === 'Air or Oxygen'
                ? renderAirO2()
                : renderRow(param, obsBands[param])
            )}
            {renderScoreRow()}
          </tbody>
        </table>
      </Box>
    </Paper>
  );
}
